/* eslint-disable no-underscore-dangle */
import {fabric} from 'fabric';
import React, {useCallback, useContext, useEffect, useState} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {ImageSize} from '~/CreateFabricInvitation/routes/EventCreateDesign/components/CardEditor/actions/ImageSize/ImageSize';
import {UploadButton} from '~/CreateFabricInvitation/routes/EventCreateDesign/components/CardEditor/actions/UploadButton/UploadButton';
import {DoneButton} from '~/CreateFabricInvitation/routes/EventCreateDesign/components/CardEditor/actions/UyoControl/Done/DoneButton';
import {UyoImageRotate} from '~/CreateFabricInvitation/routes/EventCreateDesign/components/CardEditor/actions/UyoControl/Rotate/UyoImageRotate';
import {ContentBase} from '~/CreateFabricInvitation/routes/EventCreateDesign/components/CardEditor/Mobile/Contents/ContentBase';
import {menuMappingKeys} from '~/CreateFabricInvitation/routes/EventCreateDesign/constants';
import {EventCreateContext} from '~/CreateFabricInvitation/routes/EventCreateDesign/EventCreateContext';
import {selectActiveCardEditor} from '~/CreateFabricInvitation/reducers/fabricDesign/selectors';
import {useMatchQuery} from '~library/hooks/useMatchQuery';
import {largishAndUp} from '~libSass/base/_exports.sass';
import {UPDATE_PRISTINE_UYO} from '~/CreateFabricInvitation/reducers/fabricDesign/constants';
import {DeleteButton} from '../../actions/DeleteButton/DeleteButton';
import {ResetImageXY} from '../../actions/ResetImageXY/ResetImageXY';
import './UyoEditor.sass';

export const UyoEditor = ({
  scrollToTop,
  setShowMenu,
  showMenu,
  activeMenu,
  setActiveMenu,
  uyoRef,
  noContentBase = false,
}) => {
  const {canvas, activeObjects} = useContext(EventCreateContext);
  const isUyoSelected = activeObjects?.[0]?.type === 'uYOImage';
  const {matches: isDesktop} = useMatchQuery(`(min-width: ${largishAndUp})`);
  const dispatch = useDispatch();
  const [hasActiveImage, setHasActiveImage] = useState(false);
  const [id, setId] = useState(null);
  const [uyoImage, setUyoImage] = useState(null);
  const [isUyo, setIsUyo] = useState(isUyoSelected);
  const activeCardEditor = useSelector(selectActiveCardEditor);
  let imgObjs;
  let textObjs;

  useEffect(() => {
    if (!activeObjects) {
      // can't use fabricArt when we need to setActiveObject for some reason
      imgObjs = canvas?.getObjects().filter((obj) => obj.type === 'uYOImage');
    }
    if (activeObjects?.[0]?.type === 'uYOImage' || imgObjs) {
      const [activeImage] = activeObjects || imgObjs;

      setIsUyo(true);
      setUyoImage(activeImage);
      setId(activeImage?.id);
    }
  }, [activeObjects]);

  const onCloseUyoHandler = useCallback(() => {
    scrollToTop();
    setShowMenu(false);

    canvas.discardActiveObject();
    canvas.renderAll();
  }, [canvas, scrollToTop]);

  const onResetClick = useCallback(
    (currElement) => {
      const scale = fabric.util.findScaleToCover(currElement, currElement._originalObject);

      const {left, top, angle} = currElement._originalObject;
      activeObjects[0].scale(scale).set({left, top, angle});
      canvas.renderAll();
    },
    [canvas]
  );

  const imageUploadedCallback = useCallback(
    (uploadedUyo) => {
      dispatch({
        type: UPDATE_PRISTINE_UYO,
        payload: {pristineUyo: false, id, objects: canvas.getObjects()},
      });
      canvas.setActiveObject(uploadedUyo).renderAll();
    },
    [canvas]
  );

  useEffect(() => {
    if (!showMenu && isUyo) {
      if (!activeObjects) {
        // can't use fabricArt when we need to setActiveObject for some reason
        imgObjs = canvas?.getObjects().filter((obj) => obj.type === 'uYOImage');
        textObjs = canvas?.getObjects().filter((obj) => obj.type === 'textbox');

        if (imgObjs && activeCardEditor !== 'text') {
          setShowMenu(true);
          setActiveMenu(menuMappingKeys.UYO);
          canvas?.setActiveObject(imgObjs?.[0]).renderAll();
        } else if (textObjs) {
          setShowMenu(true);
          setActiveMenu('text');
          canvas?.setActiveObject(textObjs?.[0]).renderAll();
        }
      }
    }

    const element = canvas?.getActiveObject();

    if (element?.type === 'uYOImage' || imgObjs) {
      if (!element) {
        // when MW user clicks open the photo upload menu button)
        canvas?.setActiveObject(imgObjs?.[0]).renderAll();
      }
      setHasActiveImage(true);
    } else {
      setHasActiveImage(false);
    }
  }, [isUyo, activeMenu, activeObjects, showMenu, canvas]);

  if (isDesktop || !isUyo) {
    return null;
  }

  const editorContent = (
    <>
      <ResetImageXY
        disabled={!hasActiveImage}
        label="Reset"
        dataQaId="step1-imagexy-reset"
        uyoImage={uyoImage}
      />
      <div className="uyo_control__edit-actions">
        <ImageSize id={id} />
        <UyoImageRotate btnText="Rotate" disabled={!hasActiveImage} />
      </div>
      <div className="uyo_control__remove-actions" ref={uyoRef}>
        <UploadButton showFullWidth id={id} btnText="Upload" callback={imageUploadedCallback} />
        <DeleteButton btnText="Remove" disabled={!hasActiveImage} />
        <DoneButton />
      </div>
    </>
  );

  if (noContentBase) return editorContent;

  return (
    <ContentBase
      title="PHOTO EDITOR"
      onCloseClick={onCloseUyoHandler}
      visible={activeMenu === menuMappingKeys.UYO}
      onResetClick={onResetClick}
      resetDataQaId="reset-photo-editor"
    >
      {editorContent}
    </ContentBase>
  );
};
